import React from "react";

import FormFieldLabel from "../../../../components/common/FormFieldLabel";
import { HouseholdMember } from "./YouthProfileCard";

import "../../../../css/cards.css";
import "../../../../css/forms.css";
import "../../../../css/buttons.css";

export interface Consent {
  consentTypeCode: string;
  consentTypeName: string;
  question: string;
  granted: boolean | null;
}

interface ConsentCardProps {
  compact?: boolean;
  child: HouseholdMember;
  consents: Consent[];
  onChange: (value: Consent[]) => void;
}

export const createDefaultConsents = (): Consent[] => [
  {
    consentTypeCode: "PHOTOGRAPHY",
    consentTypeName: "Photo Consent",
    question: "Can photos/videos be taken and used for promotional purposes?",
    granted: null,
  },
  {
    consentTypeCode: "EMERGENCY_TREATMENT",
    consentTypeName: "Emergency Consent",
    question:
      "Do you give permission for emergency medical assistance if required?",
    granted: null,
  },
  {
    consentTypeCode: "LEAVE_INDEPENDENTLY",
    consentTypeName: "Leave Consent",
    question: "Can this young person leave without collection?",
    granted: null,
  },
  {
    consentTypeCode: "DATA_SHARING",
    consentTypeName: "Sharing of Data",
    question: "Can information be stored and shared for service delivery?",
    granted: null,
  },
];

const getDisplayName = (child: HouseholdMember) =>
  `${child.firstName} ${child.lastName}`.trim() || "Young Person";

const consentHelpText: Record<string, string> = {
  PHOTOGRAPHY:
    "We often take photographs/videos of club activities.\n\nThese may be used for:\n- Advertising\n- Monitoring\n- Newsletters\n- Social media\n\nPlease indicate whether permission is given for photographs/videos of the young person.",
  EMERGENCY_TREATMENT:
    "I consent to my child/young person receiving any medical treatment considered necessary by a doctor in an emergency.",
  LEAVE_INDEPENDENTLY:
    "I understand that my child/young person (of high school age) will NOT be prevented from leaving the grounds or coming and going as they please during a session unless this has been discussed previously.",
  DATA_SHARING:
    "Information collected through the Youth Registration process is used for monitoring, analysis and data collection purposes.\n\nInformation may be shared with relevant partners involved in delivering youth services.",
};

export default function ConsentCard({
  child,
  compact = false,
  consents,
  onChange,
}: ConsentCardProps) {
  const consentGroupId = React.useId();
  const updateConsent = (consentTypeCode: string, granted: boolean) => {
    onChange(
      consents.map((consent) =>
        consent.consentTypeCode === consentTypeCode
          ? { ...consent, granted }
          : consent,
      ),
    );
  };

  if (compact) {
    return (
      <div className="card consent-card--compact">
        <div className="card-header"><div className="card-title">Consents</div></div>
        <p className="family-consent-hint">Choose Yes or No for each permission.</p>
        <div className="family-consent-list">
          {consents.map((consent) => (
            <div className="family-consent-row" key={consent.consentTypeCode}>
              <div id={`${consentGroupId}-${consent.consentTypeCode}-label`}>
                <FormFieldLabel component="span" label={consent.consentTypeName}
                  helpText={consentHelpText[consent.consentTypeCode]} />
                <p className="field-hint">{consent.question}</p>
              </div>
              <div className="family-consent-options" role="radiogroup"
                aria-labelledby={`${consentGroupId}-${consent.consentTypeCode}-label`}>
                {[true, false].map((granted) => (
                  <label key={String(granted)}>
                    <input type="radio" name={`${consentGroupId}-${consent.consentTypeCode}`}
                      checked={consent.granted === granted}
                      onChange={() => updateConsent(consent.consentTypeCode, granted)} />
                    {granted ? "Yes" : "No"}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Consents</div>
        {!compact && <div className="field-hint">{getDisplayName(child)}</div>}
      </div>

      <div className="form-row">
        {consents.map((consent) => (
          <div className="form-group" key={consent.consentTypeCode}>
            <FormFieldLabel
              label={consent.consentTypeName}
              helpText={consentHelpText[consent.consentTypeCode]}
            />
            <div className="field-hint">{consent.question}</div>
            <div className="button-row button-row-left" style={{ marginTop: 0 }}>
              <button
                type="button"
                className={`action-button compact ${
                  consent.granted === true ? "" : "secondary"
                }`}
                onClick={() => updateConsent(consent.consentTypeCode, true)}
              >
                Yes
              </button>
              <button
                type="button"
                className={`action-button compact ${
                  consent.granted === false ? "" : "secondary"
                }`}
                onClick={() => updateConsent(consent.consentTypeCode, false)}
              >
                No
              </button>
            </div>
            {consent.granted === null && (
              <div className="field-hint">Select yes or no</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
